// The one reading of a refused model save on the Agents page (#5210, epic #5200).
//
// `PUT|DELETE /v1/llm-routes/*` refuses in four ways the page answers differently,
// and two of them wear the SAME status: `pinRefusal` (`apps/api/src/routes/
// llm-routes.ts`) serves every refused pairing as 422 `unprocessable`, and only the
// `detail` prefix `reasoning_effort refused:` says it is the thinking-depth refusal —
// the one a "reset thinking depth" can cure. Branching on the status alone would
// offer that cure for a refusal it cannot fix, so the prefix is read here, once.
//
// Pure: an error in, a discriminated refusal out. No i18n — the card owns the copy.
// (`account_changed` is the one kind no error maps to: the save controller raises it.)

import { ApiError } from './api';

/** Who won a stale race, from the 409's `last_actor` (`ConfigLastActor`, `@developerz/mcp`). */
export type StaleWinner =
  | { readonly kind: 'member'; readonly login: string | null }
  | { readonly kind: 'token' }
  | { readonly kind: 'unknown' };

export type RouteRefusal =
  /** 409 `config-stale`: the row moved since it was read. `cleared` = a clear won. */
  | { readonly kind: 'stale'; readonly who: StaleWinner; readonly cleared: boolean }
  /** 422 `reasoning_effort refused: …` — curable by dropping the thinking depth. */
  | { readonly kind: 'reasoning'; readonly detail: string }
  /** Any other 422: shown as the server wrote it, with no cure offered. */
  | { readonly kind: 'refused'; readonly detail: string | null }
  /** 403: this caller may not pin (`write:billing`). */
  | { readonly kind: 'locked' }
  /** Anything else — a 5xx, a network failure. */
  | { readonly kind: 'failed'; readonly detail: string | null }
  /**
   * CLIENT-SIDE, never an API answer: the page switched org after this write's intent
   * was made, so it was refused unsent (`lib/agent-model-save.ts` § tenant).
   */
  | { readonly kind: 'account_changed' };

/** The detail prefix `pinRefusal` gives the reasoning refusal, and no other. */
export const REASONING_REFUSED_PREFIX = 'reasoning_effort refused:';

function winnerOf(lastActor: unknown): StaleWinner {
  if (typeof lastActor !== 'object' || lastActor === null) return { kind: 'unknown' };
  if ('token_id' in lastActor) return { kind: 'token' };
  if ('user_id' in lastActor) {
    const login = (lastActor as { login?: unknown }).login;
    return { kind: 'member', login: typeof login === 'string' ? login : null };
  }
  return { kind: 'unknown' };
}

export function routeRefusalOf(error: unknown): RouteRefusal {
  if (!(error instanceof ApiError)) return { kind: 'failed', detail: null };
  const detail = error.detail ?? null;
  switch (error.status) {
    case 409: {
      const ext = error.extensions ?? {};
      return {
        kind: 'stale',
        who: winnerOf(ext['last_actor']),
        cleared: 'current' in ext && ext['current'] === null,
      };
    }
    case 422:
      return detail?.startsWith(REASONING_REFUSED_PREFIX)
        ? { kind: 'reasoning', detail }
        : { kind: 'refused', detail };
    case 403:
      return { kind: 'locked' };
    default:
      return { kind: 'failed', detail };
  }
}
